import React, { useEffect } from "react";
import { ProfileData } from "@/hooks/useProfiles";
import ProfilePageContent from "./ProfilePageContent";
import ProfileLoading from "./ProfileLoading";
import ProfileError from "./ProfileError";
import {
  generateProfileStructuredData,
  getProfessionFromSkills,
} from "@/utils/structuredDataUtils";
import { usePortfolioItemsQuery } from "@/hooks/profile/usePortfolioItemsQuery";
import { useQueryClient } from "@tanstack/react-query";

interface ProfileDetailContentProps {
  profile: ProfileData | null;
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  isSimplifiedView: boolean;
  placeholderImage: string;
  onOpenHireMeModal: () => void;
  onOpenPrimaryHireMeModal: () => void;
  onSetHireMeScrollPopupVisible: (visible: boolean) => void;
  openPortfolioModal?: any;
  onClosePortfolioModal?: () => void;
}

const ProfileDetailContent: React.FC<ProfileDetailContentProps> = ({
  profile,
  loading,
  error,
  onRetry,
  isSimplifiedView,
  placeholderImage,
  onOpenHireMeModal,
  onOpenPrimaryHireMeModal,
  onSetHireMeScrollPopupVisible,
  openPortfolioModal,
  onClosePortfolioModal,
}) => {
  const queryClient = useQueryClient();

  // Call hooks at the top level, before any conditional returns
  const { data: portfolioData } = usePortfolioItemsQuery(profile?.id);

  if (loading) {
    return (
      <div className="relative">
        <div className="absolute inset-0 -z-10">
          <div className="absolute warm-gradient"></div>
        </div>
        <ProfileLoading />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="relative">
        <div className="absolute inset-0 -z-10">
          <div className="absolute warm-gradient"></div>
        </div>
        <ProfileError error={error} onRetry={onRetry} />
      </div>
    );
  }

  // Extract portfolio items and apply simplified view limit if needed
  const displayPortfolioItems = isSimplifiedView
    ? (portfolioData || []).slice(0, 3)
    : portfolioData || [];

  // Generate structured data for SEO
  const structuredData = generateProfileStructuredData(profile);

  // Enhance profile with displayed portfolio items and derived profession
  // but preserve the original full portfolio_items array
  const enhancedProfile = {
    ...profile,
    displayPortfolioItems, // Add the paginated items as a separate property
    derivedProfession: getProfessionFromSkills(profile),
  };

  return (
    <ProfilePageContent
      profile={enhancedProfile}
      placeholderImage={placeholderImage}
      isSimplifiedView={isSimplifiedView}
      structuredData={structuredData}
      onOpenHireMeModal={onOpenHireMeModal}
      onOpenPrimaryHireMeModal={onOpenPrimaryHireMeModal}
      onSetHireMeScrollPopupVisible={onSetHireMeScrollPopupVisible}
      openPortfolioModal={openPortfolioModal}
      onClosePortfolioModal={onClosePortfolioModal}
    />
  );
};

export default ProfileDetailContent;
